import { TIMELINE_SYSTEM_EVENT_TYPES } from '../constants';
// Conversation and Message data structures as plain objects

// Example of a message object
// {
//   id: 'msg-1',
//   content: 'Hello!',
//   sender: 'Alice',
//   timestamp: new Date()
// }

// Example of a conversation object
// {
//   id: 'conv-1',
//   title: 'Chat with Bob',
//   messages: [messageObj1, messageObj2, ...]
// }

/**
 * Gets the last message from a conversation.
 * Returns null if the conversation has no messages or is invalid.
 * 
 * @param {Object} conversation - Conversation object containing an array of messages
 * @returns {Object|null} The last message object, or null if no messages exist
 */
export const getLastMessage = (conversation) => {
  if (!conversation.messages || conversation.messages.length === 0) return null;
  return conversation.messages[conversation.messages.length - 1];
};

const normalizeTimestampSeconds = (value) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return null;
  }

  const inSeconds = value > 1e12 ? value / 1000 : value;
  const normalized = Math.floor(inSeconds);
  return Number.isNaN(normalized) ? null : normalized;
};

const getMessageTimestamp = (message) => {
  if (!message) return null;

  const candidates = [message.timestamp, message.ts, message.time];
  for (const candidate of candidates) {
    const normalized = normalizeTimestampSeconds(candidate);
    if (normalized !== null) {
      return normalized;
    }
  }

  if (typeof message.getTs === 'function') {
    const normalized = normalizeTimestampSeconds(message.getTs());
    if (normalized !== null) {
      return normalized;
    }
  }

  const originTs = message?.event?.origin_server_ts;
  const normalized = normalizeTimestampSeconds(originTs);
  return normalized;
};

const getMessageSender = (message) => {
  if (!message) return null;
  if (typeof message.sender === 'string' && message.sender) {
    return message.sender;
  }
  if (typeof message.getSender === 'function') {
    const sender = message.getSender();
    if (sender) {
      return sender;
    }
  }
  return null;
};

export const groupMessages = (messages, timeThresholdSeconds = 60) => {
  if (!Array.isArray(messages) || messages.length === 0) {
    return [];
  }

  const groups = [];
  let currentGroup = [];

  messages.forEach((message) => {
    const prevMessage = currentGroup.length > 0 ? currentGroup[currentGroup.length - 1] : null;
    const currentSender = getMessageSender(message);
    const prevSender = getMessageSender(prevMessage);
    const currentTimestamp = getMessageTimestamp(message);
    const prevTimestamp = getMessageTimestamp(prevMessage);

    const exceedsThreshold = prevTimestamp !== null && currentTimestamp !== null
      ? Math.abs(currentTimestamp - prevTimestamp) > timeThresholdSeconds
      : false;

    const missingPrevTimestamp = prevTimestamp === null && currentTimestamp !== null;

    // Timeline system events should always be in their own group
    const isSystemEvent = message.getType && TIMELINE_SYSTEM_EVENT_TYPES.includes(message.getType());
    const prevIsSystemEvent = prevMessage?.getType && TIMELINE_SYSTEM_EVENT_TYPES.includes(prevMessage.getType());

    if (!prevMessage || 
        currentSender !== prevSender || 
        exceedsThreshold || 
        missingPrevTimestamp ||
        isSystemEvent ||
        prevIsSystemEvent) {
      if (currentGroup.length > 0) {
        groups.push(currentGroup);
      }
      currentGroup = [message];
    } else {
      currentGroup.push(message);
    }
  });

  if (currentGroup.length > 0) {
    groups.push(currentGroup);
  }

  return groups;
};
